import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Coffee, MapPin } from "lucide-react";
import { api } from "../lib/api";

const formatDate = (iso) => {
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const DEFAULT_SECTION = {
  eyebrow: "Where to Find Us",
  heading: "Upcoming pop-ups around Memphis.",
  subheading: "Follow along for this week's locations.",
  instagram_label: "Follow on Instagram",
  instagram_url: "#",
  bento_image_url:
    "https://images.pexels.com/photos/27598565/pexels-photo-27598565.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bento_eyebrow: "Out & About",
  bento_title: "Bottled here. Shared with the city.",
  empty: {
    enabled: true,
    title: "We're between pop-ups at the moment.",
    body: "Check back soon to see where Memphis Coffee Co is headed next!",
  },
};

export const PopUps = () => {
  const [popups, setPopups] = useState([]);
  const [section, setSection] = useState(DEFAULT_SECTION);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([api.get("/popups"), api.get("/settings")]);
        if (cancelled) return;
        setPopups(pRes.data || []);
        if (sRes.data?.popups_section) setSection(sRes.data.popups_section);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Could not load pop-ups");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const showInstagram = section.instagram_url && section.instagram_label;
  const noPopups = !loading && !err && popups.length === 0;
  const showEmptyState = noPopups && section.empty?.enabled;

  return (
    <section id="popups" data-testid="popups-section" className="relative py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            {section.eyebrow && (
              <div data-testid="popups-eyebrow" className="text-xs uppercase tracking-[0.18em] text-[#8D99AE] mb-4">
                {section.eyebrow}
              </div>
            )}
            <h2
              data-testid="popups-heading"
              className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C1E16] leading-tight"
            >
              {section.heading}
            </h2>
            {section.subheading && (
              <p data-testid="popups-subheading" className="mt-4 text-base sm:text-lg text-[#5C4E46]">
                {section.subheading}
              </p>
            )}
          </div>
          {showInstagram && (
            <a
              data-testid="popups-instagram-link"
              href={section.instagram_url}
              target={section.instagram_url.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer noopener"
              className="self-start text-sm font-medium text-[#2C1E16] underline decoration-[#D4A373] underline-offset-4 hover:decoration-[#2C1E16] transition-colors"
            >
              {section.instagram_label} →
            </a>
          )}
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {section.bento_image_url && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="sm:col-span-2 lg:col-span-1 lg:row-span-2 rounded-3xl overflow-hidden bg-[#E8E3DD] relative min-h-[18rem]"
            >
              <img
                data-testid="popups-image"
                src={section.bento_image_url}
                alt={section.bento_title || "Pop-up image"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#2C1E16]/70 to-transparent">
                {section.bento_eyebrow && (
                  <div data-testid="popups-bento-eyebrow" className="text-[11px] uppercase tracking-[0.18em] text-[#FAF9F6]/80">
                    {section.bento_eyebrow}
                  </div>
                )}
                {section.bento_title && (
                  <div data-testid="popups-bento-title" className="font-display text-xl text-[#FAF9F6] mt-1">
                    {section.bento_title}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {loading &&
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                data-testid={`popup-skeleton-${i}`}
                className="rounded-3xl border border-[#E8E3DD] bg-white p-6 animate-pulse h-44"
              />
            ))}

          {err && !loading && (
            <div
              data-testid="popups-error"
              className="col-span-full rounded-2xl border border-[#E8E3DD] bg-white p-6 text-[#5C4E46]"
            >
              Couldn&rsquo;t load pop-ups right now. Please check back soon.
            </div>
          )}

          {showEmptyState && (
            <motion.div
              data-testid="popups-empty-state"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="sm:col-span-2 rounded-3xl border border-dashed border-[#D4A373]/60 bg-white p-8 sm:p-10 flex flex-col items-start gap-4 shadow-[0_8px_30px_rgb(0,0,0,0.03)]"
            >
              <div className="inline-flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#D4A373]/15">
                  <Coffee size={20} strokeWidth={1.5} className="text-[#7A4E1F]" />
                </span>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8D99AE]">Between Pop-ups</div>
              </div>
              {section.empty?.title && (
                <h3 className="font-display text-2xl sm:text-3xl text-[#2C1E16] leading-tight">
                  {section.empty.title}
                </h3>
              )}
              {section.empty?.body && (
                <p className="text-[#5C4E46] leading-relaxed max-w-xl">
                  {section.empty.body}
                </p>
              )}
              {showInstagram && (
                <a
                  data-testid="popups-empty-instagram"
                  href={section.instagram_url}
                  target={section.instagram_url.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-[#2C1E16] bg-[#FAF9F6] border border-[#E8E3DD] hover:bg-[#F3EDE4] rounded-full px-5 py-2.5 transition-colors"
                >
                  {section.instagram_label} →
                </a>
              )}
            </motion.div>
          )}

          {!loading &&
            !err &&
            popups.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`popup-card-${p.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: "easeOut" }}
                className="rounded-3xl border border-[#E8E3DD] bg-white p-6 sm:p-7 hover:-translate-y-1 transition-transform"
              >
                <div className="flex items-start gap-2 text-[#2C1E16]">
                  <MapPin size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <div>
                    <div className="font-display text-xl leading-tight">{p.location}</div>
                    <div className="text-sm text-[#8D99AE] mt-0.5">{p.address}</div>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[#5C4E46] text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={15} strokeWidth={1.5} />
                    {formatDate(p.date)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={15} strokeWidth={1.5} />
                    {p.time}
                  </span>
                </div>

                <div className="mt-5 pt-5 border-t border-[#E8E3DD]">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-[#8D99AE] mb-2">
                    Flavors available
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(p.flavors || []).map((f) => (
                      <span key={f} className="text-xs text-[#2C1E16] bg-[#D4A373]/15 px-3 py-1 rounded-full">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
        </div>
      </div>
    </section>
  );
};
